import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../services/api';

export const SEARCH_DIMENSIONS = [
  { key: 'eventos',    label: 'Eventos' },
  { key: 'tareas',     label: 'Tareas' },
  { key: 'armados',    label: 'Armados' },
  { key: 'desarmados', label: 'Desarmados' },
  { key: 'clientes',   label: 'Clientes' },
  { key: 'lugares',    label: 'Lugares' },
];

export const DEFAULT_DIMENSIONS = ['eventos'];
const MIN_LENGTH = 2;
const DEBOUNCE_MS = 250;
const PAGE_SIZE = 30;

export default function useAgendaSearch({ query, dimensions, includeArchived }) {
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  const trimmed = (query || '').trim();
  const active = trimmed.length >= MIN_LENGTH && dimensions.length > 0;
  const dimsKey = dimensions.join(',');

  // Cada respuesta lleva el número de pedido que la originó. Sin esto, una
  // búsqueda lenta que arrancó antes puede pisar el resultado de otra más
  // nueva: al tipear rápido se ven los resultados de un prefijo viejo.
  const requestSeq = useRef(0);

  const fetchPage = useCallback(async (offset) => {
    const seq = ++requestSeq.current;
    const params = new URLSearchParams({
      q: trimmed,
      in: dimsKey,
      limit: String(PAGE_SIZE),
      offset: String(offset),
    });
    if (includeArchived) params.set('archived', '1');
    const { data } = await api.get(`/agenda/search?${params.toString()}`);
    return { data, stale: seq !== requestSeq.current };
  }, [trimmed, dimsKey, includeArchived]);

  useEffect(() => {
    if (!active) {
      requestSeq.current++;
      setResults([]); setTotal(0); setLoading(false); setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    const timer = setTimeout(() => {
      fetchPage(0)
        .then(({ data, stale }) => {
          if (stale) return;
          setResults(data.results || []);
          setTotal(data.total || 0);
          setLoading(false);
        })
        .catch(err => {
          setError(err.response?.data?.error || 'No se pudo buscar');
          setLoading(false);
        });
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [active, fetchPage]);

  const loadMore = useCallback(() => {
    if (loadingMore || results.length >= total) return;
    setLoadingMore(true);
    fetchPage(results.length)
      .then(({ data, stale }) => {
        if (!stale) {
          setResults(prev => [...prev, ...(data.results || [])]);
          setTotal(data.total || 0);
        }
        setLoadingMore(false);
      })
      .catch(() => setLoadingMore(false));
  }, [fetchPage, loadingMore, results.length, total]);

  return { active, results, total, loading, loadingMore, error, loadMore, hasMore: results.length < total };
}
